import React, { useEffect } from "react";
import { X as CloseIcon } from "lucide-react";

/**
 * Full-screen image viewer used by every public campaign card.
 *
 * Use case: a synagogue uploads a poster-style portrait image with text
 * baked in (PAF, contact, story). The compact card on the landing /
 * organisations / "toutes les campagnes" pages crops that image to
 * cover the available rectangle — the donor sees only a slice.
 *
 * Clicking the magnifier on a card opens this lightbox, which shows
 * the image with object-contain (no crop) over a dark backdrop.
 *
 * UX rules:
 *  - Click on backdrop OR close button OR press Escape → close
 *  - Click on the image itself → no-op (don't close, let the donor
 *    pinch-zoom on mobile or right-click → save)
 *  - Body scroll is locked while open so the page underneath doesn't
 *    move when the donor scrolls the lightbox
 *  - On open we also disable the swipe-carousel behaviour by letting
 *    the parent decide (this component is purely presentational)
 */
export function CampaignImageLightbox({ src, alt, open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    // Lock body scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // ESC to close
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !src) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 cursor-zoom-out"
      style={{ animation: "lightboxFadeIn 180ms ease-out" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image de la campagne en grand"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose && onClose();
        }}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 h-11 w-11 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md text-white inline-flex items-center justify-center transition-colors"
        aria-label="Fermer"
      >
        <CloseIcon className="h-5 w-5" />
      </button>
      <img
        src={src}
        alt={alt || ""}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl cursor-default"
      />
      <style>{`
        @keyframes lightboxFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

export default CampaignImageLightbox;
